// Browser-side OpenTelemetry logs bootstrap.
//
// Mirrors tracer.ts: same resource, same environment gating. Bridges
// `console.warn`/`error` (and optionally other levels) to OTel log records
// that ship to the BFF's `/diag/logs` (a cross-origin keep-alive fetch, like
// the trace exporter — see tracer.ts), which forwards them to the in-cluster
// collector
// and into Kibana under the same `service.name` and `trace.id` as the
// browser-side traces — so a Discover search by `trace.id` returns the
// browser console output alongside the BFF/Java spans for that request.
//
// Filtering policy:
//   - Default: capture `console.warn` + `console.error` only. Browser
//     `console.log/info/debug` are silenced from the OTel pipeline to keep
//     log volume manageable (devs still see them in DevTools).
//   - Override: set `NEXT_PUBLIC_OTEL_LOG_CONSOLE=true` to capture all
//     console levels (useful for short debug sessions).
//
// Trace-context correlation:
//   The OTel API's `LogRecord.context` is automatically populated from the
//   active span context at emit time, so each shipped log carries the same
//   `trace_id` and `span_id` as whatever fetch/render is in flight. No
//   manual context wiring is required.
//
// Sentry coexistence:
//   Sentry's browser SDK also captures `console.error` as breadcrumbs.
//   That's beneficial duplication, not collision — Sentry stores them on
//   the error event, OTel stores them as standalone log records in Kibana.

import { logs } from "@opentelemetry/api-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchLogRecordProcessor,
  LoggerProvider,
} from "@opentelemetry/sdk-logs";

import { bffBaseUrl } from "@/lib/bffBase";
import { isBffAllowedPageOrigin } from "@/lib/bffTransport";
import { resolveClientTelemetry } from "@/lib/telemetryGates";

import { safeStringify } from "../safeStringify";
import { toastVariantFromConsoleArgs } from "../toastBridgeMarker";

// ---------------------------------------------------------------------------
// Environment gating — shared with instrumentation-client.ts.
// ---------------------------------------------------------------------------

// Predicate in `lib/telemetryGates.ts` (off in mock mode too). The stored
// mock-mode flag is only read in the browser; the boot block below is
// window-guarded as well.
const telemetry = resolveClientTelemetry();
const env = telemetry.environment;
/** Exported so a test can prove this boot site honours mock mode. */
export const isOtelEnabled = telemetry.otel;

// ---------------------------------------------------------------------------
// Idempotency guard — separate from tracer's so they can initialize
// independently if one is disabled.
// ---------------------------------------------------------------------------

declare global {
  var __UP_OTEL_LOGS_INITIALIZED__: boolean | undefined;
}

// ---------------------------------------------------------------------------
// Level filtering.
//
// Severity numbers follow the OTel spec (api-logs):
//   1-4  TRACE,  5-8  DEBUG,  9-12 INFO,  13-16 WARN,  17-20 ERROR,  21-24 FATAL.
// We use the canonical mid-range value (2, 6, 10, 14, 18) per level.
// ---------------------------------------------------------------------------

const CAPTURE_ALL_CONSOLE_LEVELS =
  process.env.NEXT_PUBLIC_OTEL_LOG_CONSOLE === "true";

type ConsoleLevel = "log" | "info" | "debug" | "warn" | "error";

const LEVEL_TO_SEVERITY: Record<ConsoleLevel, number | undefined> = {
  log: CAPTURE_ALL_CONSOLE_LEVELS ? 9 : undefined,
  info: CAPTURE_ALL_CONSOLE_LEVELS ? 9 : undefined,
  debug: CAPTURE_ALL_CONSOLE_LEVELS ? 5 : undefined,
  warn: 13,
  error: 17,
};

const LEVEL_TO_SEVERITY_TEXT: Record<ConsoleLevel, string> = {
  log: "INFO",
  info: "INFO",
  debug: "DEBUG",
  warn: "WARN",
  error: "ERROR",
};

// ---------------------------------------------------------------------------
// Toast-bridge severity.
//
// `lib/toast.ts` escalates its console bridge to `console.error` for EVERY
// variant when Mixpanel is recording (the replay feed only surfaces error-level
// console lines). If we took Kibana severity from the console METHOD, every
// success/info/warning toast would land as ERROR on the log dashboards. Instead
// we derive severity from the toast's TRUE variant (carried in the structured
// JSON second arg) so surface #4 (OTel/Kibana) severity is IDENTICAL to what it
// was before escalation, regardless of which console method the bridge used.
//
//   error   → ERROR (17), as via console.error today
//   warning → WARN  (13), as via console.warn today
//   other   → INFO  (9), but ONLY when CAPTURE_ALL_CONSOLE_LEVELS — matching
//             today's console.info behavior (success/info/message/default/
//             loading toasts are silenced from OTel by default). `null` → skip.
// ---------------------------------------------------------------------------

const toastVariantSeverity = (
  variant: string
): { severityNumber: number; severityText: string } | null => {
  switch (variant) {
    case "error":
      return { severityNumber: 17, severityText: "ERROR" };
    case "warning":
      return { severityNumber: 13, severityText: "WARN" };
    default:
      return CAPTURE_ALL_CONSOLE_LEVELS
        ? { severityNumber: 9, severityText: "INFO" }
        : null;
  }
};

// ---------------------------------------------------------------------------
// PII scrubbing — mirrors SENSITIVE_QUERY_KEYS from tracer.ts. Applied to
// the rendered log body before emit, since browser console messages can
// contain values that the trace URL scrubber doesn't reach.
// ---------------------------------------------------------------------------

const SENSITIVE_QUERY_KEYS = [
  // Credentials
  "password",
  "passcode",
  "passphrase",
  "secret",
  "token",
  "accessToken",
  "refreshToken",
  "apiKey",
  "authorization",
  "cookie",
  // One-time codes
  "otp",
  "otop",
  "oneTimeCode",
  "verificationCode",
  "mfaCode",
  // Card data (PCI)
  "cardNumber",
  "pan",
  "cvv",
  "cvc",
  "cardCvv",
  // Bank identifiers
  "iban",
  "accountNumber",
  "bankAccountNumber",
  "sortCode",
  "routingNumber",
  "bicSwift",
];

// Compiled once at module load — scrubLogBody runs on every bridged console
// call, and rebuilding these per call was the hot cost. Sharing a /g regex is
// safe here: String.prototype.replace resets `lastIndex` before matching.
const SENSITIVE_KV_REPLACERS: ReadonlyArray<{
  regex: RegExp;
  replacement: string;
}> = SENSITIVE_QUERY_KEYS.map((key) => ({
  regex: new RegExp(`\\b${key}\\s*[:=]\\s*"?[^"&\\s,}]+"?`, "gi"),
  replacement: `${key}=[REDACTED]`,
}));

/** Exported for unit tests only. */
export const scrubLogBody = (text: string): string => {
  let scrubbed = text;
  // Redact PAN-like 13-19 digit runs.
  scrubbed = scrubbed.replace(/\b\d{13,19}\b/g, "[PAN]");
  // Redact email-like strings.
  scrubbed = scrubbed.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[EMAIL]");
  // Redact `key=value` and `"key":"value"` patterns for sensitive keys.
  for (const { regex, replacement } of SENSITIVE_KV_REPLACERS) {
    scrubbed = scrubbed.replace(regex, replacement);
  }
  return scrubbed;
};

// ---------------------------------------------------------------------------
// Initialise LoggerProvider + console patches once per page session.
// ---------------------------------------------------------------------------

// Same page-origin gate as tracer.ts: an origin the BFF's CORS list does not
// admit could not deliver a single export.
if (
  typeof window !== "undefined" &&
  isOtelEnabled &&
  isBffAllowedPageOrigin(window.location.origin) &&
  !globalThis.__UP_OTEL_LOGS_INITIALIZED__
) {
  globalThis.__UP_OTEL_LOGS_INITIALIZED__ = true;

  // -------------------------------------------------------------------------
  // Resource — must match tracer.ts so logs and traces share `service.name`.
  // -------------------------------------------------------------------------

  const resource = resourceFromAttributes({
    "service.name": "endor-group/customer-web",
    "service.version": process.env.NEXT_PUBLIC_SENTRY_RELEASE ?? "unknown",
    "deployment.environment.name": env,
  });

  // -------------------------------------------------------------------------
  // OTLP/HTTP JSON exporter → the BFF's /diag/logs. The base ends in `/`
  // (next.config.mjs), so the path is resolved against it.
  // -------------------------------------------------------------------------

  const exporter = new OTLPLogExporter({
    url: new URL("diag/logs", bffBaseUrl()).href,
  });

  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor({
        exporter,
        maxQueueSize: 100,
        maxExportBatchSize: 20,
        scheduledDelayMillis: 5_000,
        exportTimeoutMillis: 10_000,
      }),
    ],
  });

  logs.setGlobalLoggerProvider(loggerProvider);

  // -------------------------------------------------------------------------
  // Console bridge — patches the configured console methods to ALSO emit an
  // OTel log record. Original method still runs so devtools output is
  // unaffected.
  // -------------------------------------------------------------------------

  const logger = loggerProvider.getLogger("browser-console");

  const formatArg = (arg: unknown): string => {
    if (typeof arg === "string") return arg;
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ""}`;
    }
    // safeStringify never throws (circular refs → "[Circular]", deep objects →
    // "[Unserializable]"), so a console arg can't blow up the OTel bridge.
    return safeStringify(arg);
  };

  const patch = (method: ConsoleLevel): void => {
    const severity = LEVEL_TO_SEVERITY[method];
    if (severity === undefined) return; // filtered out
    // Intentional console bridge: read + reassign the console method by name so
    // each call also emits an OTel log record. `no-console` (which allows only
    // warn/error) can't tell `method` is constrained to a known level here.
    // eslint-disable-next-line no-console
    const original = console[method].bind(console);
    // eslint-disable-next-line no-console
    console[method] = (...args: unknown[]) => {
      original(...args);
      try {
        // A toast-bridge line arrives via whatever console method lib/toast.ts
        // used — which is escalated to console.error when Mixpanel records. Keep
        // Kibana severity tied to the toast's TRUE variant so escalation never
        // reclassifies success/warning toasts as ERROR, and non-error variants
        // stay silenced by default (matching pre-escalation behavior). Non-toast
        // lines keep the method-derived severity exactly as before.
        const variant = toastVariantFromConsoleArgs(args);
        const resolved =
          variant !== null
            ? toastVariantSeverity(variant)
            : {
                severityNumber: severity,
                severityText: LEVEL_TO_SEVERITY_TEXT[method],
              };
        if (resolved === null) return; // toast variant filtered out of OTel
        const body = scrubLogBody(args.map(formatArg).join(" "));
        logger.emit({
          severityNumber: resolved.severityNumber,
          severityText: resolved.severityText,
          body,
          attributes: { "log.source": "browser-console" },
        });
      } catch {
        // Never let telemetry break the user's session.
      }
    };
  };

  patch("log");
  patch("info");
  patch("debug");
  patch("warn");
  patch("error");

  // -------------------------------------------------------------------------
  // Flush on pagehide — same belt-and-braces pattern as tracer.ts so
  // batched log records aren't lost when the user navigates away.
  // -------------------------------------------------------------------------

  window.addEventListener("pagehide", () => {
    // forceFlush() can reject from an internal race in @opentelemetry/sdk-logs:
    // BatchLogRecordProcessorBase._flushAll awaits `this._currentExport
    // .exportCompleted`, which throws when `_currentExport` is null (no export
    // in flight — common during an unload flush). A best-effort flush must never
    // surface as an unhandled promise rejection (Sentry CUSTOMER-UI-3X). Swallow
    // it — telemetry must not break the user's session.
    loggerProvider.forceFlush().catch(() => {});
  });
}
